import { AspAgingBucketKey, AspAgingSummary } from 'vopay';
import { addYears, startOfDay, subDays, subYears } from 'date-fns';

export const AGING_BUCKETS: AspAgingBucketKey[] = [
  'Current',
  'PastDue1To30',
  'PastDue31To60',
  'PastDue61To90',
  'PastDue91Plus',
];

// Overdue is everything past its due date — the four past-due buckets.
export const PAST_DUE_BUCKETS: AspAgingBucketKey[] = AGING_BUCKETS.filter((bucket) => bucket !== 'Current');

export function isAgingBucketKey(value: string | null | undefined): value is AspAgingBucketKey {
  return AGING_BUCKETS.some((bucket) => bucket === value);
}

const ZERO_BUCKET = { Amount: 0, Count: 0 };

// Rendered when the shown currency has no open items at all.
export function zeroSummary(): AspAgingSummary {
  return {
    TotalAmount: 0,
    TotalCount: 0,
    Currency: null,
    AgingBuckets: {
      Current: ZERO_BUCKET,
      PastDue1To30: ZERO_BUCKET,
      PastDue31To60: ZERO_BUCKET,
      PastDue61To90: ZERO_BUCKET,
      PastDue91Plus: ZERO_BUCKET,
    },
  };
}

export function getOverdueAmount(summary: AspAgingSummary): number {
  return PAST_DUE_BUCKETS.reduce((sum, bucket) => sum + summary.AgingBuckets[bucket].Amount, 0);
}

// Whole-number share of the total; a zero total has no shares.
export function getBucketPercent(amount: number, total: number): number {
  if (total <= 0) return 0;

  return Math.round((amount / total) * 100);
}

// Mirrors the backend buckets exactly: past-due N days means the due date is
// N days before today. Current is anything not yet due (window capped a year
// out); 91+ reaches back five years for a finite picker value.
export function getAgingBucketRange(bucket: AspAgingBucketKey, now = new Date()): [Date, Date] {
  const today = startOfDay(now);

  const ranges: { [key in AspAgingBucketKey]: [Date, Date] } = {
    Current: [today, addYears(today, 1)],
    PastDue1To30: [subDays(today, 30), subDays(today, 1)],
    PastDue31To60: [subDays(today, 60), subDays(today, 31)],
    PastDue61To90: [subDays(today, 90), subDays(today, 61)],
    PastDue91Plus: [subYears(today, 5), subDays(today, 91)],
  };

  return ranges[bucket];
}
